import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import {
  Search as SearchIcon,
  TrendingUp as GreenArrowIcon,
  TrendingDown as RedArrowIcon,
  StickyNote2 as NotesIcon,
  Schedule as ScheduleIcon,
  Today as TodayIcon,
  EditOutlined as EditIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import moment from "moment";
import {approvePendingProgram, getPendingPrograms} from "../services/PlanSerice";
import {getTrainers} from "../services/TrainerService";
import {getErrorMessage} from "../enums/ErrorMessages";
import {useCompany} from "../contexts/CompanyContext";
import {useMessageContext} from "../contexts/MessageContext";
import {useAuth} from "../contexts/AuthContext";
import {RTL} from "../cache/CacheRtl";
import SortableTableCell from "../componenets/SortableTableCell";
import {Pagination, StatusChip} from "../componenets/ui";
import {borders, shadows} from "../componenets/ui/colors";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {KPI} from "../componenets/dashboard/coach/KpiRow";

/* ── Styled trainer autocomplete ─────────────────────────── */
const TrainerSelect = styled(Autocomplete)({
  minWidth: "160px",
  "& .MuiOutlinedInput-root": {
    color: t.text, borderRadius: t.rs,
    backgroundColor: t.white, fontSize: "12.5px", fontWeight: 500, height: "36px",
    "& .MuiOutlinedInput-notchedOutline": {borderColor: t.border},
    "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: t.green},
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: t.green, borderWidth: "1.5px"},
  },
  "& .MuiInputLabel-root": {color: t.t2, fontSize: "12.5px", "&.Mui-focused": {color: t.green}},
});

/* ── Helpers ──────────────────────────────────────────────── */
const PAGE_SIZE = 20;

const getGenderDisplay = (gender) => {
  if (gender === "FEMALE") return "נ";
  if (gender === "MALE") return "ז";
  return "-";
};

const getGenderColor = (gender) => {
  if (gender === "FEMALE") return {backgroundColor: t.redLight, color: t.redText};
  if (gender === "MALE") return {backgroundColor: t.blueLight, color: t.blueText};
  return {backgroundColor: t.bg, color: t.t3};
};

/* ── Table cell presets (match Clients) ───────────────────── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
  background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

/* ═══════════════════════════════════════════════════════════
   ProgramRenew page
   ═══════════════════════════════════════════════════════════ */
const ProgramRenew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {company} = useCompany();
  const {isAdmin} = useAuth();
  const {notificationHandler} = useMessageContext();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [selectedClients, setSelectedClients] = useState([]);
  const [trainers, setTrainers] = useState([]);

  const [f, setF] = useState({
    programs: [],
    trainer: query.get("trainer") ? +query.get("trainer") : 0,
    total: 0,
    hasNext: false,
    keyword: query.get("keyword") || "",
    page: +(query.get("page") || 0),
    sortKey: query.get("sortKey") || "createdDate",
    sortDir: query.get("sortDir") || "DESC",
    loading: true,
  });

  // URL sync
  const sync = useCallback((ns) => {
    const p = new URLSearchParams();
    if (ns.trainer) p.set("trainer", ns.trainer);
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.page > 0) p.set("page", ns.page);
    if (ns.sortKey && ns.sortKey !== "createdDate") p.set("sortKey", ns.sortKey);
    if (ns.sortDir && ns.sortDir !== "DESC") p.set("sortDir", ns.sortDir);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: true});
  }, [navigate, location.pathname]);

  const set = useCallback((patch) => setF(prev => { const ns = {...prev, page: 0, ...patch}; sync(ns); return ns; }), [sync]);
  const onSort = useCallback((field, dir) => set({sortKey: field, sortDir: dir}), [set]);

  // Debounced search
  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({keyword: val}), 300);
  }, [set]);

  // Load trainers once
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAdmin()) getTrainers().then(r => setTrainers(r.data)).catch(() => {}); }, []);

  // Fetch pending programs
  useEffect(() => {
    setF(p => ({...p, loading: true}));
    getPendingPrograms(company?.id, f.keyword || null, f.page, f.trainer, f.sortKey, f.sortDir)
        .then(r => setF(p => ({...p, programs: r.data.pendingPrograms, total: r.data.total, hasNext: r.data.hasNext, loading: false})))
        .catch(e => {
          notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
          setF(p => ({...p, loading: false}));
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.page, f.keyword, f.trainer, f.sortKey, f.sortDir, company]);

  const handleOnClick = (program) => {
    if (program.finished || program.blockIndex == null || program.weekIndex == null) {
      navigate(`/app/clients/${program.traineeId}`);
    } else {
      navigate(`/app/clients/${program.traineeId}/${program.programId}/week/${program.blockIndex}/${program.weekIndex}`);
    }
  };

  const approveProgram = (pendingId) => {
    approvePendingProgram(pendingId)
        .then(() => {
          notificationHandler.success("תוכנית אושרה בהצלחה");
          setF(p => ({...p, loading: true}));
          return getPendingPrograms(company?.id, f.keyword || null, f.page, f.trainer, f.sortKey, f.sortDir);
        })
        .then(r => setF(p => ({...p, programs: r.data.pendingPrograms, total: r.data.total, hasNext: r.data.hasNext, loading: false})))
        .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  };

  const addOrRemoveClient = (client) => {
    setSelectedClients(prev =>
        prev.some(item => item.id === client.id)
            ? prev.filter(item => item.id !== client.id)
            : [...prev, client]
    );
  };

  const pages = Math.ceil(f.total / PAGE_SIZE);
  const sevenDaysAgo = moment().subtract(7, "days").unix();
  const thisWeekCount = f.programs.filter(p => p.createdDate && p.createdDate >= sevenDaysAgo).length;

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>

        {/* ─── Title row (no header band — sits on canvas, like Clients) ─── */}
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <Box>
            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
              עדכון תוכנית
            </Typography>
            <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול בקשות עדכון תוכנית</Typography>
          </Box>
        </Box>

        {/* ─── KPI grid ────────────────────────────────── */}
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr 1fr", md: "repeat(4, 1fr)"}, gap: "14px", flexShrink: 0}}>
          <KPI
              icon={<ScheduleIcon sx={{fontSize: 22}}/>} iconBg={t.amberLight} iconColor={t.amber}
              value={f.total?.toLocaleString() ?? "—"} valueColor={t.amber}
              label="סה״כ ממתינות"
          />
          <KPI
              icon={<TodayIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
              badge="7 ימים" badgeBg={t.greenLight} badgeColor={t.greenText}
              value={thisWeekCount.toLocaleString()} valueColor={t.green}
              label="השבוע"
          />
        </Box>

        {/* ─── Table card (matches Clients DashboardTable style) ─── */}
        <Box sx={{
          background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>

          {/* Filter bar inside card */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: "7px",
            padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
            background: t.bg, flexWrap: "wrap", flexShrink: 0,
          }}>
            {/* Count pill */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              fontSize: "12px", fontWeight: 700, color: t.amberText,
              background: t.amberLight, border: `1px solid ${t.amber}33`,
              padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
            }}>
              <ScheduleIcon sx={{fontSize: 14}}/>
              {f.total} בקשות ממתינות
            </Box>

            {/* Search */}
            <Box sx={{position: "relative"}}>
              <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
              <InputBase
                  value={searchInput}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="חיפוש לפי שם..."
                  sx={{
                    width: 200,
                    padding: "6px 10px 6px 30px",
                    border: `1.5px solid ${t.border}`,
                    borderRadius: t.rs,
                    background: t.white,
                    color: t.text,
                    fontSize: "12px",
                    "& input": {padding: 0},
                    "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                  }}
              />
            </Box>

            {/* Trainer dropdown — admins only */}
            {isAdmin() && trainers.length > 0 && (
                <RTL>
                  <TrainerSelect
                      size="small"
                      options={[{id: 0, name: "הכול"}, ...trainers.sort((a, b) => a.name.localeCompare(b.name, "he"))]}
                      getOptionLabel={o => o.name || ""}
                      value={trainers.find(tr => tr.id === f.trainer) || {id: 0, name: "הכול"}}
                      onChange={(_, v) => set({trainer: v?.id || 0})}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      disableClearable
                      renderInput={p => <TextField {...p} label="מאמן" size="small"/>}
                      slotProps={{popper: {sx: {zIndex: 1300}}, paper: {sx: {borderRadius: borders.radius.md, border: borders.default, boxShadow: shadows.dropdown}}}}
                  />
                </RTL>
            )}
            <Box sx={{flex: 1}}/>
          </Box>

          {/* Table body */}
          {f.loading && !f.programs.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", py: 8}}>
                <CircularProgress size={32} sx={{color: t.green}}/>
              </Box>
          ) : !f.programs.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", py: 8, flexDirection: "column", gap: 1}}>
                <Typography sx={{color: t.t2, fontSize: "15px", fontWeight: 500}}>אין בקשות ממתינות</Typography>
                <Typography sx={{color: t.t3, fontSize: "13px"}}>נסה לשנות את הסינון</Typography>
              </Box>
          ) : (
              <>
                <TableContainer>
                  <Table sx={{tableLayout: "fixed"}}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{...th, width: 44, px: 1, textAlign: "center"}}/>
                        <SortableTableCell field="name" sortBy={f.sortKey} sortDirection={f.sortDir} onSort={onSort}
                            sx={{...th, textAlign: "right", width: 180}}>שם</SortableTableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 60, display: {xs: "none", md: "table-cell"}}}>מגדר</TableCell>
                        <TableCell sx={{...th, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>תוכנית</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 75, display: {xs: "none", md: "table-cell"}}}>מגמת שקילות</TableCell>
                        <SortableTableCell field="createdDate" sortBy={f.sortKey} sortDirection={f.sortDir} onSort={onSort}
                            sx={{...th, textAlign: "center", width: 95, display: {xs: "none", md: "table-cell"}}}>תאריך בקשה</SortableTableCell>
                        <SortableTableCell field="weekIndex" sortBy={f.sortKey} sortDirection={f.sortDir} onSort={onSort}
                            sx={{...th, textAlign: "center", width: 80, display: {xs: "none", md: "table-cell"}}}>מס שבוע</SortableTableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 85, display: {xs: "none", md: "table-cell"}}}>הערות</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 110}}>ערוך</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 100}}>אישור</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {f.programs.map((program, i) => {
                        const sel = selectedClients.some(item => item.id === program.traineeId);
                        return (
                            <TableRow key={i} onClick={() => navigate(`/app/clients/${program.traineeId}`)}
                                      sx={{cursor: "pointer", bgcolor: sel ? t.greenLight : t.white, "&:hover": {bgcolor: sel ? t.greenLight : t.bg}, transition: "background 0.15s ease"}}>
                              <TableCell sx={{...td, px: 1, width: 44, textAlign: "center"}}>
                                <Checkbox size="small" checked={sel}
                                          onClick={e => { e.stopPropagation(); addOrRemoveClient({id: program.traineeId, name: program.traineeName}); }}
                                          sx={{color: t.border, p: "4px", "&.Mui-checked": {color: t.green}}}/>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "right"}}>
                                <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                  {program.traineeName}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                <Chip label={getGenderDisplay(program.traineeGender)} size="small"
                                      sx={{minWidth: "28px", height: "24px", fontSize: "11px", fontWeight: 600, ...getGenderColor(program.traineeGender)}}/>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                                <Box sx={{overflow: "hidden"}}>
                                  <Typography sx={{fontSize: "13px", fontWeight: 500, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    {program.programName}
                                  </Typography>
                                  {program.finished && (
                                      <Typography sx={{fontSize: "11px", color: t.red}}>(סיים תוכנית)</Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5}}>
                                  <Typography sx={{fontSize: "13px", fontWeight: 500, color: program.progress > 0 ? t.green : t.red}}>
                                    {program.progress}
                                  </Typography>
                                  {program.progress > 0
                                      ? <GreenArrowIcon sx={{color: t.green, fontSize: 16}}/>
                                      : <RedArrowIcon sx={{color: t.red, fontSize: 16}}/>
                                  }
                                </Box>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                <Typography sx={{fontSize: "13px", color: t.t2}}>
                                  {program.createdDate ? moment.unix(program.createdDate).format("DD/MM/YY") : "—"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                <Typography sx={{fontSize: "13px", color: t.t2}}>
                                  {program.totalWeeks} / {program.finished ? program.totalWeeks : program.weekIndex + 1}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                {program.hasNotes ? (
                                    <RTL>
                                      <StatusChip label="יש הערות" variant="warning" size="small"
                                                  icon={<NotesIcon sx={{fontSize: "14px !important"}}/>}/>
                                    </RTL>
                                ) : (
                                    <Typography sx={{fontSize: "12px", color: t.t3}}>—</Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center"}}>
                                <Box component="button"
                                     onClick={e => { e.stopPropagation(); handleOnClick(program); }}
                                     sx={{
                                       display: "inline-flex", alignItems: "center", gap: "5px",
                                       background: t.white, color: t.t2,
                                       height: 28, px: "12px", borderRadius: t.rs,
                                       fontSize: "12px", fontWeight: 600,
                                       border: `1px solid ${t.border}`, cursor: "pointer", fontFamily: "inherit",
                                       transition: "all .14s",
                                       "&:hover": {borderColor: t.t2, color: t.text, background: t.bg},
                                     }}>
                                  <EditIcon sx={{fontSize: 14}}/>
                                  ערוך
                                </Box>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center"}}>
                                <Box component="button"
                                     onClick={e => { e.stopPropagation(); program.finished ? handleOnClick(program) : approveProgram(program.id); }}
                                     sx={{
                                       display: "inline-flex", alignItems: "center", gap: "5px",
                                       background: t.green, color: "#fff",
                                       height: 28, px: "12px", borderRadius: t.rs,
                                       fontSize: "12px", fontWeight: 600,
                                       border: "none", cursor: "pointer", fontFamily: "inherit",
                                       transition: "all .14s",
                                       "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
                                     }}>
                                  <CheckIcon sx={{fontSize: 14}}/>
                                  אשר
                                </Box>
                              </TableCell>
                            </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {pages > 1 && (
                    <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={PAGE_SIZE}
                                onPageChange={p => { const ns = {...f, page: p}; setF(ns); sync(ns); }}/>
                )}
              </>
          )}
        </Box>
      </Box>
  );
};

export default ProgramRenew;
